import type { User } from 'types';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UserAvatar } from '@/components/UserAvatar';
import { Plus, MoreHorizontal } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/Dialogs';
import { DataTable, useDataControl } from '@/components/DataTable';
import { SearchInput } from '@/components/SearchInput';
import { useTeams, type InvitePayload } from '@/state/useTeams';
import { useAuth } from 'hooks/useAuth';
import { useForm } from 'hooks/useForm';
import { USER_ROLES } from 'definitions';
import { ActionButton, SEARCH_FIELD_CLASS } from '../../ConsolePanel';

const CHIP = 'micro-label rounded-pill inline-flex w-fit border px-2 py-0.5';
const FIELD_CONTROL = 'border-line bg-field text-foreground h-10 rounded-lg text-body';

/** Elevated roles read as a selection; a plain Member is not a status worth colouring. */
const roleChip = (role: string) => {
  if (role === 'Owner') return 'border-accent-line text-accent';
  if (role === 'Super Admin') return 'border-sel-line text-sel';
  if (role === 'Admin') return 'border-sel-line text-sel';
  return 'border-line text-dim';
};

/** Active is live, Invited is waiting on someone, Blocked and Banned are broken access. */
const statusChip = (status: string) => {
  if (status === 'Active') return 'border-ok-line text-ok';
  if (status === 'Invited') return 'border-warn-line text-warn';
  if (status === 'Blocked' || status === 'Banned') return 'border-danger-line text-danger';
  return 'border-line text-dim';
};

export function SettingsAgencyTeam() {
  const { user } = useAuth();
  const { company } = user!;
  const { teamMembers, inviteTeamMember, resendInvite, blockUser, unblockUser, deleteUser } = useTeams(company.id);

  const dataController = useDataControl<User>(teamMembers || null);
  const { searchQuery, setSearchQuery } = dataController;

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    action: () => Promise<void>;
    variant?: 'default' | 'destructive';
  }>({ open: false, title: '', description: '', action: async () => {} });

  // Invite form
  const { formRef, formState, update } = useForm<Omit<InvitePayload, 'companyId'>>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'Member',
  });

  /**
   * "Send invitation" is never disabled, so the old single `if (...) return` meant pressing
   * it with a field blank did nothing whatsoever - no error, no highlight, nothing. Each
   * field is now checked on its own and named, and the dialog stays open with the rest of
   * the form still filled in.
   *
   * Checked inline rather than in a helper because `formState` is a Partial: these guards
   * are what narrows the four fields for the payload below.
   */
  const handleInvite = async (ev: React.FormEvent) => {
    ev.preventDefault();

    const { firstName, lastName, email, role } = formState;
    if (!firstName) {
      toast.error('Enter a first name for the person you are inviting.');
      return;
    }
    if (!lastName) {
      toast.error('Enter a last name for the person you are inviting.');
      return;
    }
    if (!email) {
      toast.error('Enter the email address the invitation should go to.');
      return;
    }
    if (!role) {
      toast.error('Choose a role for the new member.');
      return;
    }

    await inviteTeamMember({ email, firstName, lastName, role, companyId: company.id });
    setInviteDialogOpen(false);
    update({ email: '', firstName: '', lastName: '', role: 'Member' });
  };

  const handleAction = (
    title: string,
    description: string,
    action: () => Promise<void>,
    variant?: 'default' | 'destructive',
  ) => {
    setConfirmDialog({ open: true, title, description, action, variant });
  };

  const getMemberName = (member: User) => {
    if (member.firstName || member.lastName) {
      return `${member.firstName || ''} ${member.lastName || ''}`.trim();
    }
    return member.email.split('@')[0];
  };

  const roleOptions = USER_ROLES.filter((r) => r !== 'Super Admin' && r !== 'Owner').map((role) => ({
    value: role,
    label: role,
  }));

  return (
    <div className="flex flex-col gap-[14px]">
      <section className="bg-panel border-line rounded-panel border">
        {/* Stacked below `md`. `relative` on the search wrapper because SearchInput
            collapses to a magnifier and drops its field in absolutely. */}
        <div className="border-line flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3">
          <h2 className="text-foreground m-0 text-body font-medium" data-tour="team-heading">
            Agency Team
          </h2>
          {teamMembers && (
            <div className="flex flex-col gap-2 md:shrink-0 md:flex-row md:items-center">
              <div className="relative w-full md:w-auto">
                <SearchInput
                  placeholder="Search members..."
                  value={searchQuery}
                  handleSearch={setSearchQuery}
                  className={SEARCH_FIELD_CLASS}
                />
              </div>
              <Button
                onClick={() => setInviteDialogOpen(true)}
                className="h-9 w-full rounded-lg md:w-auto"
                data-tour="team-invite"
              >
                <Plus className="h-4 w-4" />
                Invite
              </Button>
            </div>
          )}
        </div>

        {teamMembers && (
          <div className="p-3">
            <DataTable
              dataController={dataController}
              searchKeys={['firstName', 'lastName', 'email']}
              pageSize={10}
              noDataMessage="No team members yet."
              noDataCTA={
                <Button onClick={() => setInviteDialogOpen(true)} className="mt-4 h-9 rounded-lg">
                  <Plus className="mr-2 h-4 w-4" />
                  Invite your first team member
                </Button>
              }
              columns={[
                {
                  field: 'avatar',
                  label: '',
                  headerClassName: 'micro-label w-12',
                  // Same call as the organisation team: the portrait is desk-only.
                  mobile: 'hide',
                  format: ({ item }) => <UserAvatar user={item} size="sm" />,
                },
                {
                  field: 'firstName',
                  label: 'Name',
                  sortKey: 'firstName',
                  headerClassName: 'micro-label',
                  cellClassName: 'min-w-0',
                  mobile: 'title',
                  format: ({ item }) => (
                    <span className="text-foreground block truncate font-medium" title={getMemberName(item)}>
                      {getMemberName(item)}
                    </span>
                  ),
                },
                {
                  field: 'email',
                  label: 'Email',
                  sortKey: 'email',
                  headerClassName: 'micro-label',
                  cellClassName: 'min-w-0',
                  mobile: 'meta',
                  format: ({ item }) => (
                    <div className="flex min-w-0 items-center gap-2">
                      {/* The email is the identity you act on when removing someone,
                          so a clipped one has to stay recoverable. */}
                      <span className="text-dim truncate" title={item.email}>
                        {item.email}
                      </span>
                      {item.status === 'Invited' && (
                        // Padded to a real target — bare 10px text was a ~40x16px hit
                        // area beside a truncated email.
                        <ActionButton
                          onClick={() => resendInvite(item.email)}
                          title={`Send the invitation to ${item.email} again`}
                        >
                          resend
                        </ActionButton>
                      )}
                    </div>
                  ),
                },
                {
                  field: 'role',
                  label: 'Role',
                  sortKey: 'role',
                  headerClassName: 'micro-label w-32',
                  mobile: 'meta',
                  // Anchors go on the first row only, so each tour step finds one element.
                  format: ({ value, idx }) => (
                    <span
                      className={cn(CHIP, roleChip((value as string) || ''))}
                      data-tour={idx === 0 ? 'team-role' : undefined}
                    >
                      {value as string}
                    </span>
                  ),
                },
                {
                  field: 'status',
                  label: 'Status',
                  sortKey: 'status',
                  headerClassName: 'micro-label w-32',
                  mobile: 'meta',
                  format: ({ value, idx }) => (
                    <span
                      className={cn(CHIP, statusChip((value as string) || ''))}
                      data-tour={idx === 0 ? 'team-status' : undefined}
                    >
                      {value as string}
                    </span>
                  ),
                },
                {
                  label: 'Actions',
                  headerClassName: 'micro-label w-12',
                  mobile: 'action',
                  format: ({ item, idx }) => (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-tour={idx === 0 ? 'team-row-actions' : undefined}
                          className="text-dim hover:text-foreground hover:bg-hover h-8 w-8 rounded-lg p-0"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {item.status === 'Blocked' ? (
                          <DropdownMenuItem
                            onClick={() =>
                              handleAction(
                                'Unblock User',
                                `Are you sure you want to unblock ${getMemberName(item)}?`,
                                () => unblockUser(item.id),
                              )
                            }
                          >
                            Unblock
                          </DropdownMenuItem>
                        ) : (
                          item.status === 'Active' && (
                            <DropdownMenuItem
                              onClick={() =>
                                handleAction(
                                  'Block User',
                                  `Are you sure you want to block ${getMemberName(item)}?`,
                                  () => blockUser(item.id),
                                )
                              }
                            >
                              Block
                            </DropdownMenuItem>
                          )
                        )}
                        {item.status === 'Invited' && (
                          <DropdownMenuItem onClick={() => resendInvite(item.email)}>Resend invite</DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          className="text-danger"
                          onClick={() =>
                            handleAction(
                              'Remove User',
                              `Are you sure you want to remove ${getMemberName(item)} from the team? This action cannot be undone.`,
                              () => deleteUser(item.id),
                              'destructive',
                            )
                          }
                        >
                          Remove from team
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ),
                },
              ]}
            />
          </div>
        )}
      </section>

      {/* Invite dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite team member</DialogTitle>
            <DialogDescription>Invite a new member to join the agency team.</DialogDescription>
          </DialogHeader>
          <form ref={formRef} onSubmit={handleInvite} className="space-y-4">
            {/* One column on a phone: two 150px name fields in a dialog leave neither
                its placeholder nor its value readable. */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="firstName" className="micro-label block">
                  First Name
                </label>
                <Input
                  id="firstName"
                  name="firstName"
                  placeholder="First name"
                  defaultValue={formState.firstName}
                  className={FIELD_CONTROL}
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="lastName" className="micro-label block">
                  Last Name
                </label>
                <Input
                  id="lastName"
                  name="lastName"
                  placeholder="Last name"
                  defaultValue={formState.lastName}
                  className={FIELD_CONTROL}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="email" className="micro-label block">
                Email
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Enter email address"
                defaultValue={formState.email}
                className={FIELD_CONTROL}
              />
            </div>
            <div className="space-y-1.5">
              <span className="micro-label block">Role</span>
              <Select
                name="role"
                options={roleOptions}
                value={formState.role}
                update={update}
                placeholder="Select a role"
                className={FIELD_CONTROL}
              />
            </div>
            {/* flex-wrap so a narrow dialog puts the pair on two lines rather than
                letting the primary verb run past the edge. border-input on the outline
                button: it carries no fill, so the border is all that draws it. */}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setInviteDialogOpen(false)}
                className="border-input text-foreground hover:bg-hover h-9 rounded-lg bg-transparent"
              >
                Cancel
              </Button>
              <Button type="submit" className="h-9 rounded-lg">
                Send invitation
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel="Confirm"
        confirmVariant={confirmDialog.variant}
        onConfirm={async () => {
          // `finally`, not a plain sequence: ConfirmDialog re-enables Confirm only after the
          // await resolves, so a failed block or removal used to leave this dialog open with
          // a permanently greyed-out Confirm. No toast is added here on purpose - useTeams
          // goes through useClient, whose error dialog already names the failure, and two
          // reports of one problem is worse than one.
          try {
            await confirmDialog.action();
          } finally {
            setConfirmDialog({ ...confirmDialog, open: false });
          }
        }}
      >
        {null}
      </ConfirmDialog>
    </div>
  );
}
